<template>
  <div class="version version_m">
    <h4 class="version__title" v-if="version[0] != '0'">v {{ version[0] }}</h4>
    <div
      class="version__item"
      :class="{'version__item_center': repo}"
      v-for="item in version[1]"
      :key="item.id"
    >
      <div
        class="version__install-wrapper"
        v-if="repo || item.di_compatible && canBeInstalled"
      >
        <a
          :class="{ version__install_pending: item.id === idPending }"
          @click.prevent="install(item.id)"
          class="version__install"
          href="#"
        >
          <img class="version__install-icon" src="@/assets/icon-install.svg" />
          {{ $t("INSTALL") }}
          <template v-if="item.id === idPending">
            <div class="version__loader">
              <div class="version__loader-dot"></div>
              <div class="version__loader-dot"></div>
              <div class="version__loader-dot"></div>
            </div>
            <div class="version__tooltip">{{ $t("REQUESTING") }}...</div>
          </template>
        </a>
      </div>

      <div class="version__desc">
        <div class="version__row">
          <a
            v-if="repo"
            class="version__link"
            :class="{
              version__link_verified: item.verified,
            }"
            :href="item.link"
            target="_blank"
          >
            {{ item.host }}
            <img class="version__open" src="@/assets/icon-open.svg" />
          </a>
          <a
            v-else
            class="version__link"
            :class="{
              version__link_disabled: getLinkDescription(item.link).disabled,
              version__link_verified: item.verified,
            }"
            @click.prevent="open(item.link, item.host)"
          >
            {{ item.host }}
            <img class="version__open" src="@/assets/icon-open.svg" />
          </a>
          <a
            v-if="!repo && !item.protected"
            @click.prevent="reportLink(item.id)"
            href="#"
            class="version__report"
            >{{ $t("REPORT_LINK_BUTTON") }}</a
          >
        </div>

        <span v-if="item.cracker || item.uploader_name" class="version__text">
          <template v-if="item.cracker">
            {{ $t("CRACKED BY") }} {{ item.cracker }} • </template
          ><template v-if="item.uploader_name">
            {{ $t("UPLOADED BY") }} {{ item.uploader_name }}
          </template>
        </span>
        <div v-if="item.reports && item.reports.length" class="version__warning">
          <span class="version__warning-icon">
            <img src="@/assets/icon-warning.svg" />
          </span>
          <span v-html="$t('LINK_REPORTED', { reason: item.reports[0].reason })"></span>
        </div>
        <html-with-links
          v-if="item.is_compatible"
          class="version__badge"
          :class="{
            version__badge_warning: item.is_compatible.result === 'unknown',
            version__badge_success: item.is_compatible.result === 'yes',
            version__badge_error: item.is_compatible.result === 'no',
          }"
          :html="
            getCompatibleMessage(
              item.is_compatible.reason,
              item.is_compatible.result
            )
          "
        ></html-with-links>
      </div>
    </div>
  </div>
</template>

<script>
import installAppMixin from '@/mixins/installApp';
import HtmlWithLinks from '@/components/HtmlWithLinks';

export default {
  mixins: [installAppMixin],
  components: {
    HtmlWithLinks,
  },
  props: {
    type: String,
    version: Array,
    idPending: [Number, String],
    repo: Boolean,
  },
  computed: {
    canBeInstalled() {
      return this.type === 'ios' || this.type === 'cydia' || this.type === 'tvos' || this.type === 'books';
    },
  },
  methods: {
    async open(link, host) {
      const isOnion = /\.onion$/i.test(host);

      if (isOnion) {
        await this.$confirm(this.$t('TOR_LINK_ERROR'));
      }

      const desc = this.getLinkDescription(link);

      if (desc.href) {
        // eslint-disable-next-line no-restricted-globals
        location.href = desc.href;
        return;
      }

      if (desc.ticket) {
        this.$router.push(`/redirect/${desc.ticket}`);
      }
    },
    getLinkDescription(link) {
      if (link === '@') {
        return { disabled: true };
      }

      try {
        const url = new URL(link);

        if (url.protocol === 'ticket:') {
          return { ticket: url.pathname.slice(2) || url.host };
        }

        return { href: link };
      } catch {
        return { disabled: true };
      }
    },
    async install(id) {
      if (this.idPending) {
        return;
      }

      this.$emit('install', id);

      if (!this.repo) {
        this.installApp(id, this.$route.params.type || this.type).finally(() => { this.$emit('install', 0); });
        return;
      }

      const version = this.version[1].find(v => v.id === id);

      this.installApp(id, this.type, {
        isFromRepo: true,
        name: version.name,
        link: version.link,
        image: version.image,
      }).finally(() => { this.$emit('install', 0); });
    },
    reportLink(id) {
      if (this.checkLinking(this.$t('LINK_TO_REPORT'))) {
        return;
      }

      this.$prompt(this.$t('REPORT_NOTICE', { href: 'https://telegra.ph/App-installation-troubleshooting-03-20' })).then(reason => this.$api.reportLink({
        type: this.$route.params.type,
        id,
        reason,
      })).then(() => {
        this.$notify({
          type: 'success',
          text: this.$t('REPORT_SUBMITTED'),
        });
      });
    },
    checkLinking(error) {
      if (!this.$store.getters.isDeviceLinked) {
        this.$confirm(error).then(() => {
          this.$router.push('/link');
        });
      }

      return !this.$store.getters.isDeviceLinked;
    },
    getCompatibleMessage(reason, status) {
      if (reason) {
        return reason;
      }

      if (status === 'yes') {
        return this.$t('COMPATIBLE_WITH_DEVICE');
      }

      if (status === 'no') {
        return this.$t('NOT_COMPATIBLE_WITH_DEVICE');
      }

      return this.$t('COMPATIBILITY_UNKNOWN');
    },
  },
};
</script>

<style lang="scss" scoped>
.version {
  &_m {
    margin: 20px 0 10px;
  }

  &__title {
    color: var(--text-tertiary);
    font-weight: 600;
    margin-bottom: 10px;
  }

  &__item {
    display: flex;

    &_center {
      align-items: center;
    }

    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }

  &__install {
    display: flex;
    align-items: center;
    color: var(--text-primary);
    padding: 5px 8px;
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    position: relative;

    &-icon {
      margin-right: 5px;
      filter: var(--img-filter);
    }

    &-wrapper {
      margin-right: 8px;
    }

    &_pending {
      pointer-events: none;

      .version__loader {
        display: flex;
      }

      .version__tooltip {
        display: block;
      }
    }
  }

  &__loader {
    display: none;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: var(--bg);
    align-items: center;
    justify-content: center;

    &-dot {
      display: inline-block;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      margin-right: 3px;
      background: var(--text-primary);
      animation: wave 1.3s linear infinite;

      &:nth-child(2) {
        animation-delay: -1.1s;
      }

      &:nth-child(3) {
        animation-delay: -0.9s;
      }
    }

    @keyframes wave {
      0%,
      60%,
      100% {
        transform: initial;
      }

      30% {
        transform: translateY(-4px);
      }
    }
  }

  &__tooltip {
    display: none;
    position: absolute;
    left: -1px;
    top: -25px;
    color: #ffffff;
    font-size: 12px;
    background: #4e4e4e;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.12);
    border-radius: 2px;
    white-space: nowrap;
    font-weight: 400;
    padding: 3px 5px;

    &:before {
      content: "";
      display: block;
      position: absolute;
      border-style: solid;
      border-width: 5px 4.5px 0 4.5px;
      border-color: #4e4e4e transparent transparent transparent;
      bottom: -5px;
      left: 15px;
    }
  }

  &__link {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    margin-right: 6px;
    word-break: break-word;
    color: $app-link-color;

    &::v-deep a {
      color: $app-link-color;
    }

    &_verified {
      filter: brightness(0) saturate(100%) invert(28%) sepia(62%)
        saturate(5528%) hue-rotate(115deg) brightness(96%) contrast(82%);
    }

    &_disabled {
      filter: brightness(0);
      pointer-events: none;
    }
  }

  &__open {
    margin-left: 3px;
    margin-top: 2px;
  }

  &__report {
    text-decoration: underline;
    color: $text-secondary;
    font-size: 12px;
    white-space: nowrap;
  }

  &__warning {
    display: flex;
    padding: 4px;
    border-radius: 2.8px;
    background-color: var(--bg-error);
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 5px;
    word-break: break-word;

    &-icon {
      padding-top: 1px;
      margin-right: 4px;

      img {
        width: 11px;
      }
    }
  }

  &__text {
    display: block;
    font-size: 12px;
    margin: 5px 0;
    line-height: 15px;
  }

  &__badge {
    display: inline-block;
    font-size: 10px;
    line-height: 12px;
    padding: 4px;
    border-radius: 2px;

    &_success {
      color: #053305;
      background-color: var(--bg-success);
    }

    &_warning {
      color: #2b2b05;
      background-color: var(--bg-warning);
    }

    &_error {
      color: var(--configure-error-text);
      background-color: var(--bg-error);
    }
  }
}
</style>
